import { useBoolean } from '@sravni/react-utils';
import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';

import { checkIsShowAccordionBlockEditIcon } from 'shared/lib';
import { useAppSelector } from 'shared/lib/redux';
import type { IFormHeader } from 'shared/types/IFormHeader';
import { AnketaFormPopup } from 'shared/ui/AnketaFormPopup';
import { CustomAccordionItem } from 'shared/ui/CustomAccordion';

import { selectCreditPurposeInfo } from 'entities/creditForm';

import { CreditPurposeInfoModalTexts } from '../../CreditPurposeInfo.texts';
import { useAccordionHeaderParams } from '../../lib/useAccordionHeaderParams';
import { selectCreditPurposeFormViewOptions } from '../../model/creditPurposeFormView.selectors';
import type { TMobileAppBlocks } from '../../types/mobileAppBlocks';
import { Form as FormElement } from '../Form/Form';

export type { TMobileAppBlocks } from '../../types/mobileAppBlocks';

export type TCreditPurposeAccordionProps = TAccordionProps & {
  shouldRestoreAdditionalData?: boolean;
  productCardBlock?: React.ReactNode;
  productTabTitle?: string;
  mobileApp?: TMobileAppBlocks;
};

export const AccordionModalForm: FC<TCreditPurposeAccordionProps> = ({
  isOpen,
  shouldForceOpenPopup = false,
  isFormForceOpened = false,
  onFormSubmit,
  isLoading,
  shouldRestoreAdditionalData,
  isDisableEdit = false,
  shouldScrollIntoAccordion,
  isPopupClosable = false,
  onPopupClose,
  children,
  isSummary,
  stepIndex,
  formId,
  icon,
  submitButtonText,
  isHideDivider = false,
  accordionIconSize,
  TitleComponent,
  DescriptionComponent,
  defaultDescription,
  defaultTitle,
  isAlwaysShowDescription = false,
  onPopupOpen,
  isAnyFormStepOpenedInPopup,
  analyticsOverrides,
  productCardBlock,
  productTabTitle: productTabTitleProp,
  mobileApp,
}) => {
  const { data, isFullFilled, isOpenedInPopup } = useAppSelector(selectCreditPurposeInfo);
  const viewOptions = useAppSelector(selectCreditPurposeFormViewOptions);
  const [shouldShowEditPopup, setShouldShowEditPopup] = useBoolean();

  const [_, setHeader] = useState<IFormHeader>();
  const productTabTitle = productCardBlock ? productTabTitleProp : undefined;

  const [title, description] = useAccordionHeaderParams(
    data,
    {
      title: defaultTitle,
      description: defaultDescription,
    },
    isOpen,
    {
      isCreditCardsForm: viewOptions.isCreditCardForm,
      productTabTitle,
    },
  );
  const popupTitle = productTabTitle ?? defaultTitle;

  const isWithEditItem = checkIsShowAccordionBlockEditIcon(
    isOpen,
    isSummary || isFullFilled,
    isDisableEdit,
    shouldForceOpenPopup,
  );

  const isAccordionOpen = !isFullFilled && isOpen;
  const isEditPopupVisible = isOpenedInPopup || shouldShowEditPopup || (shouldForceOpenPopup && isFormForceOpened);
  // Если shouldForceOpenPopup false, то нужно давать возможнсть отредактировать данные и не дизейблить их
  // сделано для логики короткого продления, когда при восстановлении нет валидации
  const isDisabled = !isOpen && !isFullFilled && (shouldForceOpenPopup || isDisableEdit);
  const descriptionText = useMemo(
    () => (!isDisabled || isAlwaysShowDescription ? description : ''),
    [description, isAlwaysShowDescription, isDisabled],
  );
  const isWithDivider = !isSummary && !isHideDivider;

  const onEditButtonClick = useCallback(() => {
    onPopupOpen?.();
    setShouldShowEditPopup.on();
  }, [onPopupOpen, setShouldShowEditPopup]);

  const onSubmitPopup: IFormPopup['onFormSubmit'] = useCallback(
    async (formData, isDialog) => {
      await onFormSubmit?.(formData, isDialog);
      setShouldShowEditPopup.off();
      onPopupClose?.(true);
    },
    [onFormSubmit, onPopupClose, setShouldShowEditPopup],
  );

  const PopupFormElement = useCallback<FC<IFormPopup>>(
    (formProps) => (
      <FormElement
        {...formProps}
        formId={formId}
        shouldRestoreAdditionalData={!!shouldRestoreAdditionalData}
        productCardBlock={productCardBlock}
        productTabTitle={productTabTitleProp}
        mobileApp={mobileApp}
      />
    ),
    [formId, mobileApp, productCardBlock, productTabTitleProp, shouldRestoreAdditionalData],
  );

  return (
    <>
      <CustomAccordionItem
        onEditButtonClick={onEditButtonClick}
        icon={icon}
        iconSize={accordionIconSize}
        isOpen={isAccordionOpen}
        disabled={isDisabled}
        withEdit={isWithEditItem}
        title={TitleComponent ? <TitleComponent disabled={isDisabled}>{title}</TitleComponent> : title}
        withDivider={isWithDivider}
        description={
          DescriptionComponent ? (
            <DescriptionComponent disabled={isDisabled}>{descriptionText}</DescriptionComponent>
          ) : (
            descriptionText
          )
        }
        scrollIntoView={isAccordionOpen && shouldScrollIntoAccordion}
      >
        <FormElement
          onFormSubmit={onFormSubmit}
          setHeader={setHeader}
          isFormForceOpened={isFormForceOpened}
          isLoading={isLoading}
          shouldRestoreAdditionalData={shouldRestoreAdditionalData}
          stepIndex={stepIndex}
          formId={formId}
          submitButtonText={submitButtonText}
          isAnyFormStepOpenedInPopup={isAnyFormStepOpenedInPopup}
          analyticsOverrides={analyticsOverrides}
          productCardBlock={productCardBlock}
          productTabTitle={productTabTitleProp}
          mobileApp={mobileApp}
        />
      </CustomAccordionItem>

      <AnketaFormPopup
        submitButtonText={submitButtonText}
        isClosable={isPopupClosable}
        isFormForceOpened
        FormElement={PopupFormElement}
        onFormSubmit={onSubmitPopup}
        onClose={(isSubmit) => {
          if (isSubmit) {
            return;
          }
          setShouldShowEditPopup.off();
          onPopupClose?.(false);
        }}
        isLoading={isLoading}
        isOpen={isEditPopupVisible}
        icon={icon}
        iconSize={accordionIconSize}
        title={TitleComponent ? <TitleComponent>{popupTitle}</TitleComponent> : popupTitle}
        subtitle={
          DescriptionComponent ? (
            <DescriptionComponent>{CreditPurposeInfoModalTexts.description}</DescriptionComponent>
          ) : (
            CreditPurposeInfoModalTexts.description
          )
        }
        setHeader={setHeader}
        stepIndex={stepIndex}
        analyticsOverrides={analyticsOverrides}
      >
        {children}
      </AnketaFormPopup>
    </>
  );
};
